import { motion } from 'framer-motion';
import React, { useRef, useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { usePxToVw } from '../../../../hooks/usePxToVw';

function Options({
  question,
  dispatch,
  answer,
  triviaQuizSettings,
  numQuestions,
  index,
  setIsCorrect,
  brandFont,
  brandColor,
}) {
  const { pxToVw } = usePxToVw();

  const hasAnswered = answer !== null;
  const [editOpen, setEditOpen] = useState(true);

  const videoRef = useRef(null);
  const [optionsAnimationComplete, setOptionsAnimationComplete] = useState(false);

  const [popupText, setPopUpText] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // const [isCorrect, setIsCorrect] = useState(null); // null = no answer, true = correct, false = incorrect

  const correctAudioRef = useRef(null);
  const incorrectAudioRef = useRef(null);

  const handleAnswer = option => {
    const isAnswerCorrect = option === question.correctAnswer;
    setIsCorrect(isAnswerCorrect);
    dispatch({ type: 'newAnswer', payload: option });

    // Play correct or incorrect video
    if (isAnswerCorrect) {
      setPopUpText('Correct!');

      correctAudioRef.current?.play();
    } else {
      incorrectAudioRef.current?.play();
      setPopUpText('Incorrect');
    }

    setEditOpen(true);

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setOptionsAnimationComplete(false);
    }

    //setHoveredIndex(null);
  };

  const handleVideoPlayPage = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.play();
    }
  };

  const handleVideoEnd = () => {
    // videoRef.current.play();

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setEditOpen(true);
      setOptionsAnimationComplete(false);
      setPopUpText('Sorry, your time has expired.');
      //

      setTimeout(() => {
        dispatch({ type: 'newAnswer', payload: null });
        moveNextQuestion();
        setIsCorrect(null);
        setEditOpen(false);
      }, 5000); // 5-second delay
    }
  };

  const moveNextQuestion = () => {
    setIsCorrect(null);
    if (index < numQuestions - 1) {
      dispatch({ type: 'nextQuestion' });
      setEditOpen(false);
      setHoveredIndex(null);
    } else {
      dispatch({ type: 'finish' });
      setEditOpen(false);
    }
  };

  const angleInRadians = (triviaQuizSettings?.answerSettings?.shadow?.angle * Math.PI) / 180;
  const offsetX = Math.cos(angleInRadians) * triviaQuizSettings?.answerSettings?.shadow?.distance;
  const offsetY = Math.sin(angleInRadians) * triviaQuizSettings?.answerSettings?.shadow?.distance;

  const style = {
    borderRadius: pxToVw(triviaQuizSettings?.answerSettings?.cornerRadius),
    height: pxToVw(triviaQuizSettings?.answerSettings?.height),
    width: pxToVw(triviaQuizSettings?.answerSettings?.width),
    textAlign: triviaQuizSettings?.answerSettings?.textAlign,
    marginBottom: pxToVw(triviaQuizSettings?.answerSettings?.marginBottom),
    borderColor: isHovered
      ? triviaQuizSettings?.answerSettings?.highlight?.borderColor
      : triviaQuizSettings?.answerSettings?.outlineColor,
    borderWidth: triviaQuizSettings?.answerSettings?.outlineThickness + 'px',
    borderStyle: 'solid',
    backgroundColor: isHovered
      ? triviaQuizSettings?.answerSettings?.highlight?.fillColor
      : triviaQuizSettings?.answerSettings?.fillColor,
    color: isHovered
      ? triviaQuizSettings?.answerSettings?.highlight?.fontColor
      : triviaQuizSettings?.answerSettings?.fontColor,
    fontFamily: triviaQuizSettings?.answerSettings?.font,
    fontSize: pxToVw(triviaQuizSettings?.answerSettings?.fontSize),
    boxShadow: `${offsetX}px ${offsetY}px ${triviaQuizSettings?.answerSettings?.shadow?.size}px rgba(
      ${parseInt(triviaQuizSettings?.answerSettings?.shadow?.color.slice(1, 3), 16)}, 
      ${parseInt(triviaQuizSettings?.answerSettings?.shadow?.color.slice(3, 5), 16)}, 
      ${parseInt(triviaQuizSettings?.answerSettings?.shadow?.color.slice(5, 7), 16)}, 
      ${triviaQuizSettings?.answerSettings?.shadow?.alpha}
    )`,
    transition: '0.2s ease-in-out',
    cursor: 'pointer',
  };

  useEffect(() => {
    setHoveredIndex(null);
  }, [question]); // Reset hover state when question changes

  return (
    <div>
      <motion.div
        key={question._id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          // alignItems: 'center',
          gap: '2vw',
          marginTop: '5vw',
          // marginBottom: "40px",
          //  width: triviaQuizSettings?.answerSettings?.width + 'px'
          width: '100%',
        }}
      >
        {question.options.map((option, index) => (
          <motion.button
            key={index}
            className={`btn btn-option ${answer === option ? 'answer' : ''} ${
              hasAnswered ? (answer === question.correctAnswer ? 'correct' : 'wrong') : ''
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            // transition={{ delay: 1 + index * 0.20, duration: 0.8, ease: "bounceOut" }}
            transition={{ stiffness: 20, damping: 30, delay: 1 + index * 0.15, duration: 0.8 }}
            disabled={hasAnswered}
            onClick={() => handleAnswer(option)}
            onAnimationComplete={() => {
              console.log('Animation complete for index:', index);
              if (index === question.options.length - 1 && !hasAnswered) {
                setOptionsAnimationComplete(true);
                handleVideoPlayPage();
              }
            }}
            style={{
              ...style,
              lineHeight: '1.4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              borderColor:
                hoveredIndex === index
                  ? triviaQuizSettings?.answerSettings?.highlight?.borderColor
                  : triviaQuizSettings?.answerSettings?.outlineColor,
              backgroundColor:
                hoveredIndex === index
                  ? triviaQuizSettings?.answerSettings?.highlight?.fillColor
                  : triviaQuizSettings?.answerSettings?.fillColor,
              color:
                hoveredIndex === index
                  ? triviaQuizSettings?.answerSettings?.highlight?.fontColor
                  : triviaQuizSettings?.answerSettings?.fontColor,
            }}
            // onMouseEnter={() => {
            //   if(optionsAnimationComplete){
            //     setHoveredIndex(index);

            //   }
            // } }
            // onMouseLeave={() => setHoveredIndex(null)}
            onFocus={() => setHoveredIndex(index)} // Handles keyboard navigation focus
            onBlur={() => setHoveredIndex(null)} // Removes highlight when losing focus
          >
            {hasAnswered && answer !== question.correctAnswer ? (
              question.correctAnswer === option ? (
                <>
                  {option}{' '}
                  <i
                    className="fa fa-check-circle"
                    style={{
                      color: 'green',
                      fontSize: 11 + 'vw',
                      position: 'absolute',
                      right: '5px',
                      top: '4px',
                    }}
                  ></i>
                </>
              ) : (
                option
              )
            ) : (
              option
            )}
          </motion.button>
        ))}
        {editOpen && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto',
              // marginTop: '20px',
              // marginBottom:"20px",
              maxWidth: pxToVw(triviaQuizSettings?.answerSettings?.width),
              backgroundColor: '#FFFFFF', // White modal background
              border: `2px solid ${brandColor}`,
              padding: `${pxToVw(triviaQuizSettings?.quizPadding)}`,
              borderRadius: '5vw',
              boxShadow: style?.boxShadow,
              width: '100%',
              height: pxToVw(triviaQuizSettings?.answerSettings?.height),
            }}
          >
            <div
              style={{
                textAlign: 'center',
                fontSize: `${pxToVw(triviaQuizSettings?.quizFontSize)}`,
                fontFamily: brandFont,
                lineHeight: `${triviaQuizSettings?.quizLineHeight}`,
              }}
            >
              {popupText}
            </div>
          </div>
        )}

        {optionsAnimationComplete && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '2vw',
              maxWidth: pxToVw(triviaQuizSettings?.answerSettings?.width),
            }}
          >
            <motion.video
              ref={videoRef}
              // className="videoEle show"
              src={triviaQuizSettings?.questionTimerVideo?.fileUrl}
              muted
              preload="auto"
              autoPlay
              onPlay={handleVideoPlayPage}
              onEnded={handleVideoEnd}
              loop={false}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              style={{
                maxWidth: '100%',
                // maxHeight: "400px",
                objectFit: 'contain',
              }}
            />
          </div>
        )}
      </motion.div>

      <audio
        ref={correctAudioRef}
        src={triviaQuizSettings?.correctResponse?.fileUrl}
        onEnded={moveNextQuestion}
      />
      <audio
        ref={incorrectAudioRef}
        src={triviaQuizSettings?.wrongResponse?.fileUrl}
        onEnded={moveNextQuestion}
      />
      {/* Correct or Incorrect GIFs */}

      {/*       
        <Dialog
          open={null}    
        
          onClose={null} // Prevent closing
          sx={{
            "& .MuiDialog-paper": {
              backgroundColor: "#FFFFFF", // White modal background
              border:`2px solid ${brandColor}`,              
              padding: `${triviaQuizSettings?.quizPadding}px`,
              borderRadius:"20px",
             // width:"1000px"
              // marginLeft:`${triviaQuizSettings?.quizsetting?.leftoffset}%`
            },
            "& .MuiBackdrop-root": {
              backgroundColor: "transparent", // Change this to any color (Example: Red with opacity)
            },
          
          }}
        >
          <DialogTitle style={{textAlign:"center",fontSize:`${triviaQuizSettings?.quizFontSize}px`,fontFamily:brandFont,lineHeight:`${triviaQuizSettings?.quizLineHeight}`}}>
            {popupText}
            </DialogTitle>
          
          
        
          <DialogActions >
                      
          </DialogActions>
          
      </Dialog> */}
    </div>
  );
}

export default Options;
